import React, { useState, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Card,
  CardContent,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
  CircularProgress,
} from '@mui/material';
import {
  Search as SearchIcon,
  LocalShipping as LocalShippingIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Assignment as AssignmentIcon,
  Visibility,
  Edit,
  Close,
  Person,
  Save,
  Add,
  PlayArrow,
  Pause,
} from '@mui/icons-material';

const OrderDispatch = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filters, setFilters] = useState({
    fromDate: '',
    toDate: '',
    status: '',
    priority: '',
    searchTerm: '',
  });
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [hasSearched, setHasSearched] = useState(false);
  const [editFormData, setEditFormData] = useState({
    dispatchStatus: '',
    priority: '',
    trackingNumber: '',
    courierName: '',
    estimatedDelivery: '',
    notes: '',
  });

  const sampleData = [
    {
      sno: 1,
      orderId: 'ORD001',
      orderDate: '2025-01-01',
      customerName: 'Amit Gupta',
      customerPhone: '+91 98765 43210',
      productName: 'Samsung Galaxy S24',
      quantity: 2,
      totalAmount: 150000,
      dispatchStatus: 'Dispatched',
      priority: 'High',
      trackingNumber: 'TRK001234567',
      courierName: 'Blue Dart',
      estimatedDelivery: '2025-01-03',
      deliveryAddress: '123 Main St, Mumbai, Maharashtra',
      dispatchDate: '2025-01-01',
      notes: 'Handle with care - fragile item',
    },
    {
      sno: 2,
      orderId: 'ORD002',
      orderDate: '2025-01-02',
      customerName: 'Priya Sharma',
      customerPhone: '+91 98765 43211',
      productName: 'iPhone 15 Pro',
      quantity: 1,
      totalAmount: 120000,
      dispatchStatus: 'Ready for Dispatch',
      priority: 'Medium',
      trackingNumber: '',
      courierName: '',
      estimatedDelivery: '',
      deliveryAddress: '456 Park Ave, Delhi, NCR',
      dispatchDate: '',
      notes: '',
    },
    {
      sno: 3,
      orderId: 'ORD003',
      orderDate: '2025-01-03',
      customerName: 'Rajesh Kumar',
      customerPhone: '+91 98765 43212',
      productName: 'OnePlus 12',
      quantity: 3,
      totalAmount: 165000,
      dispatchStatus: 'In Transit',
      priority: 'High',
      trackingNumber: 'TRK001234568',
      courierName: 'DTDC',
      estimatedDelivery: '2025-01-05',
      deliveryAddress: '789 Tech Park, Bangalore, Karnataka',
      dispatchDate: '2025-01-03',
      notes: 'Customer requested morning delivery',
    },
    {
      sno: 4,
      orderId: 'ORD004',
      orderDate: '2025-01-04',
      customerName: 'Sneha Patel',
      customerPhone: '+91 98765 43213',
      productName: 'Google Pixel 8',
      quantity: 1,
      totalAmount: 65000,
      dispatchStatus: 'Delivered',
      priority: 'Low',
      trackingNumber: 'TRK001234569',
      courierName: 'FedEx',
      estimatedDelivery: '2025-01-06',
      deliveryAddress: '321 Garden St, Pune, Maharashtra',
      dispatchDate: '2025-01-04',
      notes: 'Delivered successfully',
    },
    {
      sno: 5,
      orderId: 'ORD005',
      orderDate: '2025-01-05',
      customerName: 'Vikram Singh',
      customerPhone: '+91 98765 43214',
      productName: 'Xiaomi 14',
      quantity: 2,
      totalAmount: 90000,
      dispatchStatus: 'Pending',
      priority: 'Medium',
      trackingNumber: '',
      courierName: '',
      estimatedDelivery: '',
      deliveryAddress: '654 Market Rd, Chennai, Tamil Nadu',
      dispatchDate: '',
      notes: '',
    },
  ];

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Edit form handlers
  const handleEditFormChange = (field, value) => {
    setEditFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveEdit = () => {
    // Here you would typically make an API call to update the dispatch
    console.log('Saving dispatch:', selectedOrder?.orderId, editFormData);
    
    // For demo purposes, we'll just close the dialog
    // In a real app, you'd update the dispatch in your state/API
    setEditDialogOpen(false);
    setSelectedOrder(null);
    setEditFormData({
      dispatchStatus: '',
      priority: '',
      trackingNumber: '',
      courierName: '',
      estimatedDelivery: '',
      notes: '',
    });
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setFilters({ ...filters, searchTerm: '' });
    setHasSearched(false);
    setPage(0);
  };

  // Search handler - applies all filters
  const handleSearch = () => {
    setSearchLoading(true);
    setHasSearched(true);
    console.log('Search triggered with filters:', {
      searchTerm: searchTerm,
      fromDate: filters.fromDate,
      toDate: filters.toDate,
      status: filters.status,
      priority: filters.priority
    }); // Debug log
    // Simulate API call delay
    setTimeout(() => {
      setFilters(prev => ({ ...prev, searchTerm: searchTerm }));
      setSearchLoading(false);
    }, 500);
  };

  // View and Edit action handlers
  const handleView = (order) => {
    setSelectedOrder(order);
    setViewDialogOpen(true);
  };

  const handleEdit = (order) => {
    setSelectedOrder(order);
    setEditFormData({
      dispatchStatus: order.dispatchStatus,
      priority: order.priority,
      trackingNumber: order.trackingNumber,
      courierName: order.courierName,
      estimatedDelivery: order.estimatedDelivery,
      notes: order.notes,
    });
    setEditDialogOpen(true);
  };

  const handleCloseViewDialog = () => {
    setViewDialogOpen(false);
    setSelectedOrder(null);
  };

  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setSelectedOrder(null);
    setEditFormData({
      dispatchStatus: '',
      priority: '',
      trackingNumber: '',
      courierName: '',
      estimatedDelivery: '',
      notes: '',
    });
  };

  // Optimized filter function with memoization - only works when search is performed
  const getFilteredData = useCallback(() => {
    // If no search has been performed, return all data
    if (!hasSearched) {
      return sampleData;
    }

    let filteredData = [...sampleData];

    // Apply search filter
    if (filters.searchTerm) {
      const searchLower = filters.searchTerm.toLowerCase();
      filteredData = filteredData.filter(item =>
        item.orderId.toLowerCase().includes(searchLower) ||
        item.customerName.toLowerCase().includes(searchLower) ||
        item.productName.toLowerCase().includes(searchLower) ||
        item.trackingNumber.toLowerCase().includes(searchLower) ||
        item.courierName.toLowerCase().includes(searchLower)
      );
    }

    // Apply date filters
    if (filters.fromDate) {
      filteredData = filteredData.filter(item => new Date(item.orderDate) >= new Date(filters.fromDate));
    }
    if (filters.toDate) {
      filteredData = filteredData.filter(item => new Date(item.orderDate) <= new Date(filters.toDate));
    }

    // Apply status filter
    if (filters.status) {
      filteredData = filteredData.filter(item => item.dispatchStatus === filters.status);
    }

    // Apply priority filter
    if (filters.priority) {
      filteredData = filteredData.filter(item => item.priority === filters.priority);
    }

    return filteredData;
  }, [hasSearched, filters.searchTerm, filters.fromDate, filters.toDate, filters.status, filters.priority]);

  const filteredData = useMemo(() => getFilteredData(), [getFilteredData]);
  const totalOrders = filteredData.length;
  const dispatchedOrders = filteredData.filter(item => item.dispatchStatus === 'Dispatched' || item.dispatchStatus === 'In Transit').length;
  const deliveredOrders = filteredData.filter(item => item.dispatchStatus === 'Delivered').length;
  const pendingOrders = filteredData.filter(item => item.dispatchStatus === 'Pending' || item.dispatchStatus === 'Ready for Dispatch').length;

  const getStatusColor = (status) => {
    switch (status) {
      case 'Delivered': return 'success';
      case 'In Transit': return 'info';
      case 'Dispatched': return 'primary';
      case 'Ready for Dispatch': return 'warning';
      case 'Pending': return 'error';
      default: return 'default';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'High': return 'error';
      case 'Medium': return 'warning';
      case 'Low': return 'success';
      default: return 'default';
    }
  };

  return (
    <Box sx={{ p: 3, backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#000', mb: 0.5 }}>
              Order Dispatch Management
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Track and manage order dispatch and delivery
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<Add />}
            sx={{
              backgroundColor: '#536DFE',
              '&:hover': { backgroundColor: '#3F51B5' },
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              py: 1,
              borderRadius: 2,
            }}
          >
            New Dispatch
          </Button>
        </Box>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <Card sx={{ boxShadow: '0 2px 12px rgba(0,0,0,0.1)', borderRadius: 2, p: 3, textAlign: 'center', bgcolor: '#fff' }}>
            <Box display="flex" alignItems="center" justifyContent="center" mb={2}>
              <AssignmentIcon sx={{ fontSize: 40, color: '#536DFE', mr: 1 }} />
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#000' }}>
                {totalOrders}
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
              Total Orders
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card sx={{ boxShadow: '0 2px 12px rgba(0,0,0,0.1)', borderRadius: 2, p: 3, textAlign: 'center', bgcolor: '#fff' }}>
            <Box display="flex" alignItems="center" justifyContent="center" mb={2}>
              <LocalShippingIcon sx={{ fontSize: 40, color: '#4caf50', mr: 1 }} />
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#000' }}>
                {dispatchedOrders}
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
              Dispatched
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card sx={{ boxShadow: '0 2px 12px rgba(0,0,0,0.1)', borderRadius: 2, p: 3, textAlign: 'center', bgcolor: '#fff' }}>
            <Box display="flex" alignItems="center" justifyContent="center" mb={2}>
              <CheckCircleIcon sx={{ fontSize: 40, color: '#4caf50', mr: 1 }} />
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#000' }}>
                {deliveredOrders}
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
              Delivered
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card sx={{ boxShadow: '0 2px 12px rgba(0,0,0,0.1)', borderRadius: 2, p: 3, textAlign: 'center', bgcolor: '#fff' }}>
            <Box display="flex" alignItems="center" justifyContent="center" mb={2}>
              <ScheduleIcon sx={{ fontSize: 40, color: '#ff9800', mr: 1 }} />
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#000' }}>
                {pendingOrders}
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
              Pending
            </Typography>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Card sx={{ mb: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#333', mb: 3 }}>
            Dispatch Filters
          </Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Dispatch Status</InputLabel>
                <Select 
                  label="Dispatch Status" 
                  value={filters.status}
                  onChange={(e) => {
                    setFilters({ ...filters, status: e.target.value });
                  }}
                  sx={{
                    borderRadius: 2,
                  }}
                >
                  <MenuItem value="">All Status</MenuItem>
                  <MenuItem value="Pending">Pending</MenuItem>
                  <MenuItem value="Ready for Dispatch">Ready for Dispatch</MenuItem>
                  <MenuItem value="Dispatched">Dispatched</MenuItem>
                  <MenuItem value="In Transit">In Transit</MenuItem>
                  <MenuItem value="Delivered">Delivered</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Priority</InputLabel>
                <Select 
                  label="Priority" 
                  value={filters.priority}
                  onChange={(e) => {
                    setFilters({ ...filters, priority: e.target.value });
                  }}
                  sx={{
                    borderRadius: 2,
                  }}
                >
                  <MenuItem value="">All Priority</MenuItem>
                  <MenuItem value="High">High</MenuItem>
                  <MenuItem value="Medium">Medium</MenuItem>
                  <MenuItem value="Low">Low</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                label="Search Dispatch"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by order ID, customer name, tracking number, or courier..."
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      {searchLoading ? (
                        <CircularProgress size={20} color="primary" />
                      ) : (
                        <SearchIcon />
                      )}
                    </InputAdornment>
                  ),
                  endAdornment: searchTerm && (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={handleClearSearch}
                        edge="end"
                      >
                        <Close fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleSearch();
                  }
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
            </Grid>
          </Grid>

          {/* Search Controls */}
          <Box sx={{ mt: 3, display: 'flex', gap: 2, alignItems: 'center', justifyContent: 'flex-end' }}>
            <Tooltip title="Apply all filters and search" arrow>
              <Button
                variant="contained"
                onClick={handleSearch}
                disabled={searchLoading}
                startIcon={searchLoading ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  backgroundColor: '#536DFE',
                  borderRadius: 2,
                  px: 3,
                  py: 1,
                  '&:hover': {
                    backgroundColor: '#3F51B5',
                  },
                  '&:disabled': {
                    backgroundColor: '#e0e0e0',
                    color: '#9e9e9e',
                  }
                }}
              >
                {searchLoading ? 'Searching...' : 'Search'}
              </Button>
            </Tooltip>
            <Button
              variant="outlined"
              onClick={() => {
                setFilters({
                  fromDate: '',
                  toDate: '',
                  status: '',
                  priority: '',
                  searchTerm: '',
                });
                setSearchTerm('');
                setHasSearched(false);
                setSearchLoading(false);
              }}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                borderColor: '#536DFE',
                color: '#536DFE',
                borderRadius: 2,
                px: 3,
                py: 1,
                '&:hover': {
                  borderColor: '#3F51B5',
                  backgroundColor: '#f0f7ff',
                }
              }}
            >
              Clear All
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Search Results Summary */}
      {hasSearched && (
        <Card sx={{ mb: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2, border: '1px solid #e3f2fd' }}>
          <CardContent sx={{ py: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1,
                p: 1,
                backgroundColor: '#e3f2fd',
                borderRadius: 2,
                minWidth: 'fit-content'
              }}>
                {searchLoading ? (
                  <CircularProgress size={20} color="primary" />
                ) : (
                  <SearchIcon sx={{ color: '#536DFE' }} />
                )}
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#536DFE' }}>
                  Search Results
                </Typography>
              </Box>
              <Box sx={{ flex: 1 }}>
                {searchLoading ? (
                  <Typography variant="body2" color="text.secondary">
                    Searching dispatch records...
                  </Typography>
                ) : (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                    <Typography variant="body2" color="text.primary" sx={{ fontWeight: 600 }}>
                      {filteredData.length} order{filteredData.length !== 1 ? 's' : ''} found
                    </Typography>
                    {filters.status && filters.status !== 'All Status' && (
                      <Chip
                        label={`Status: ${filters.status}`} 
                        size="small"
                        color="primary" 
                        variant="outlined"
                        sx={{ fontSize: '0.75rem' }}
                      />
                    )}
                    {filters.priority && filters.priority !== 'All Priority' && (
                      <Chip
                        label={`Priority: ${filters.priority}`} 
                        size="small"
                        color="secondary" 
                        variant="outlined"
                        sx={{ fontSize: '0.75rem' }}
                      />
                    )}
                    {filters.fromDate && (
                      <Chip 
                        label={`From: ${filters.fromDate}`} 
                        size="small" 
                        color="info" 
                        variant="outlined"
                        sx={{ fontSize: '0.75rem' }}
                      />
                    )}
                    {filters.toDate && (
                      <Chip 
                        label={`To: ${filters.toDate}`} 
                        size="small" 
                        color="info" 
                        variant="outlined"
                        sx={{ fontSize: '0.75rem' }}
                      />
                    )}
                  </Box>
                )}
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Table */}
      <Card sx={{ boxShadow: '0 2px 12px rgba(0,0,0,0.1)', borderRadius: 2, mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
            Dispatch Details
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#536DFE' }}>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>S.No</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Order ID</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Customer</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Product</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Priority</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Status</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Courier</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Tracking</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Est. Delivery</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px', textAlign: 'center' }}>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {searchLoading ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
                        <CircularProgress size={24} />
                        <Typography variant="body2" color="text.secondary">
                          Searching dispatch records...
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : filteredData.length > 0 ? (
                  filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => (
                    <TableRow 
                      key={row.sno} 
                      hover
                      sx={{
                        '&:nth-of-type(odd)': { backgroundColor: '#f9f9f9' },
                        '&:hover': { backgroundColor: '#f0f7ff !important' }
                      }}
                    >
                      <TableCell>{row.sno}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{row.orderId}</TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{row.customerName}</Typography>
                          <Typography variant="caption" color="text.secondary">{row.customerPhone}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{row.productName}</TableCell>
                      <TableCell>
                        <Chip
                          label={row.priority} 
                          size="small"
                          color={getPriorityColor(row.priority)}
                          sx={{ fontWeight: 600 }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={row.dispatchStatus} 
                          size="small"
                          color={getStatusColor(row.dispatchStatus)}
                          sx={{ fontWeight: 600 }}
                        />
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{row.courierName || '-'}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{row.trackingNumber || '-'}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{row.estimatedDelivery || '-'}</TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                          <Tooltip title="View Details">
                            <IconButton
                              size="small"
                              onClick={() => handleView(row)}
                              sx={{ color: '#536DFE' }}
                            >
                              <Visibility fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit Dispatch">
                            <IconButton
                              size="small"
                              onClick={() => handleEdit(row)}
                              sx={{ color: '#ff9800' }}
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                      <Typography variant="body1" color="text.secondary">
                        No dispatch records found matching your search criteria
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          
          {/* Pagination */}
          {filteredData.length > 0 && (
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={filteredData.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          )}
        </CardContent>
      </Card>

      {/* View Details Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={handleCloseViewDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          backgroundColor: '#536DFE',
          color: 'white',
          px: 3,
          py: 2
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LocalShippingIcon />
            <Typography variant="h6" fontWeight="bold">
              Dispatch Details
            </Typography>
          </Box>
          <IconButton 
            onClick={handleCloseViewDialog}
            sx={{ color: 'white' }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ p: 3 }}>
          {selectedOrder && (
            <>
              {/* Header Section */}
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 2, 
                mb: 3,
                p: 2,
                backgroundColor: '#f8f9fa',
                borderRadius: 2,
                border: '1px solid #e9ecef'
              }}>
                <Avatar sx={{ bgcolor: '#536DFE', width: 48, height: 48 }}>
                  <Person />
                </Avatar>
                <Box>
                  <Typography variant="h6" fontWeight="bold">
                    {selectedOrder.orderId}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Customer: {selectedOrder.customerName}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                    <Chip 
                      label={selectedOrder.dispatchStatus} 
                      size="small" 
                      color={getStatusColor(selectedOrder.dispatchStatus)}
                    />
                    <Chip 
                      label={selectedOrder.priority} 
                      size="small" 
                      color={getPriorityColor(selectedOrder.priority)}
                    />
                  </Box>
                </Box>
              </Box>

              <Grid container spacing={3}>
                {/* Order Details */}
                <Grid item xs={12} md={6}>
                  <Card sx={{ height: '100%' }}>
                    <CardContent>
                      <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <AssignmentIcon color="primary" />
                        Order Information
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Order ID:</Typography>
                          <Typography variant="body2" fontWeight="600">{selectedOrder.orderId}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Order Date:</Typography>
                          <Typography variant="body2" fontWeight="600">{selectedOrder.orderDate}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Product:</Typography>
                          <Typography variant="body2" fontWeight="600">{selectedOrder.productName}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Quantity:</Typography>
                          <Typography variant="body2" fontWeight="600">{selectedOrder.quantity}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Total Amount:</Typography>
                          <Typography variant="body2" fontWeight="600" color="success.main">₹{selectedOrder.totalAmount.toLocaleString()}</Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Dispatch Details */}
                <Grid item xs={12} md={6}>
                  <Card sx={{ height: '100%' }}>
                    <CardContent>
                      <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LocalShippingIcon color="primary" />
                        Dispatch Information
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Dispatch Status:</Typography>
                          <Chip 
                            label={selectedOrder.dispatchStatus} 
                            size="small" 
                            color={getStatusColor(selectedOrder.dispatchStatus)}
                          />
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Priority:</Typography>
                          <Chip 
                            label={selectedOrder.priority} 
                            size="small" 
                            color={getPriorityColor(selectedOrder.priority)}
                          />
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Courier:</Typography>
                          <Typography variant="body2" fontWeight="600">{selectedOrder.courierName || 'Not assigned'}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Tracking Number:</Typography>
                          <Typography variant="body2" fontWeight="600">{selectedOrder.trackingNumber || 'Not available'}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Est. Delivery:</Typography>
                          <Typography variant="body2" fontWeight="600">{selectedOrder.estimatedDelivery || 'Not set'}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Dispatch Date:</Typography>
                          <Typography variant="body2" fontWeight="600">{selectedOrder.dispatchDate || 'Not dispatched'}</Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Customer Details */}
              <Card sx={{ mt: 3 }}>
                <CardContent>
                  <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Person color="primary" />
                    Customer & Delivery Details
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Customer Name:</strong> {selectedOrder.customerName}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Phone:</strong> {selectedOrder.customerPhone}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Delivery Address:</strong> {selectedOrder.deliveryAddress}
                      </Typography>
                    </Grid>
                    {selectedOrder.notes && (
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Notes:</strong> {selectedOrder.notes}
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </CardContent>
              </Card>
            </>
          )}
        </DialogContent>
        
        <DialogActions sx={{ p: 3, backgroundColor: '#f8f9fa' }}>
          <Button
            onClick={handleCloseViewDialog}
            variant="contained"
            sx={{
              backgroundColor: '#536DFE',
              '&:hover': { backgroundColor: '#3F51B5' }
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dispatch Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={handleCloseEditDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          backgroundColor: '#ff9800',
          color: 'white',
          px: 3,
          py: 2
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Edit />
            <Typography variant="h6" fontWeight="bold">
              Edit Dispatch
            </Typography>
          </Box>
          <IconButton 
            onClick={handleCloseEditDialog}
            sx={{ color: 'white' }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ p: 3 }}>
          {selectedOrder && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
                Edit dispatch details for: <strong>{selectedOrder.orderId}</strong>
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Dispatch Status</InputLabel>
                    <Select
                      value={editFormData.dispatchStatus}
                      onChange={(e) => handleEditFormChange('dispatchStatus', e.target.value)}
                      label="Dispatch Status"
                    >
                      <MenuItem value="Pending">Pending</MenuItem>
                      <MenuItem value="Ready for Dispatch">Ready for Dispatch</MenuItem>
                      <MenuItem value="Dispatched">Dispatched</MenuItem>
                      <MenuItem value="In Transit">In Transit</MenuItem>
                      <MenuItem value="Delivered">Delivered</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Priority</InputLabel>
                    <Select
                      value={editFormData.priority}
                      onChange={(e) => handleEditFormChange('priority', e.target.value)}
                      label="Priority"
                    >
                      <MenuItem value="High">High</MenuItem>
                      <MenuItem value="Medium">Medium</MenuItem>
                      <MenuItem value="Low">Low</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Tracking Number"
                    value={editFormData.trackingNumber}
                    onChange={(e) => handleEditFormChange('trackingNumber', e.target.value)}
                    variant="outlined"
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Courier Name"
                    value={editFormData.courierName}
                    onChange={(e) => handleEditFormChange('courierName', e.target.value)}
                    variant="outlined"
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Estimated Delivery"
                    type="date"
                    value={editFormData.estimatedDelivery}
                    onChange={(e) => handleEditFormChange('estimatedDelivery', e.target.value)}
                    variant="outlined"
                    size="small"
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Notes"
                    value={editFormData.notes}
                    onChange={(e) => handleEditFormChange('notes', e.target.value)}
                    variant="outlined"
                    size="small"
                    multiline
                    rows={3}
                  />
                </Grid>
              </Grid>

              {/* Read-only fields */}
              <Box sx={{ p: 2, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  Order Information (Read-only)
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Order ID:</strong> {selectedOrder.orderId}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Customer:</strong> {selectedOrder.customerName}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Product:</strong> {selectedOrder.productName}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Quantity:</strong> {selectedOrder.quantity}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            </Box>
          )}
        </DialogContent>
        
        <DialogActions sx={{ p: 3, backgroundColor: '#f8f9fa' }}>
          <Button
            onClick={handleCloseEditDialog}
            variant="outlined"
            sx={{ mr: 1 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveEdit}
            variant="contained"
            startIcon={<Save />}
            sx={{
              backgroundColor: '#ff9800',
              '&:hover': { backgroundColor: '#f57c00' }
            }}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OrderDispatch;
